import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';

const TableLayout = ({ playerCount, layout = 'preview', selectedFriends = [], onFriendSelect }) => {
  const maxFriendsNeeded = playerCount - 1; // Current user is 1 player
  const currentSelectedCount = selectedFriends.filter(f => f).length;
  const canSelectMore = currentSelectedCount < maxFriendsNeeded;
  const [openDropdowns, setOpenDropdowns] = useState({});
  const tableSize = layout === 'preview' ? 320 : 400;
  const dropdownRefs = useRef({});

  // Import friends data
  const friends = [
    { id: 1, name: "Vedika", avatar: "👩‍🎓" },
    { id: 2, name: "Spruha", avatar: "👩‍🎓" },
    { id: 3, name: "Dhruvi", avatar: "👩‍🎓" },
    { id: 4, name: "Manasvi", avatar: "👩‍🎓" },
    { id: 5, name: "Swarnika", avatar: "👩‍🎓" },
    { id: 6, name: "Shayana", avatar: "👩‍🎓" },
    { id: 7, name: "Priya", avatar: "👩‍🎓" },
    { id: 8, name: "Amit", avatar: "👨‍🎓" },
    { id: 9, name: "Sarah", avatar: "👩‍🎓" },
    { id: 10, name: "Mike", avatar: "👨‍🎓" }
  ];

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      Object.keys(openDropdowns).forEach(index => {
        if (openDropdowns[index] && dropdownRefs.current[index] && !dropdownRefs.current[index].contains(event.target)) {
          setOpenDropdowns(prev => ({
            ...prev,
            [index]: false
          }));
        }
      });
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openDropdowns]);

  const toggleDropdown = (index) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const handleFriendSelect = (index, friend, event) => {
    // Prevent event bubbling
    event.stopPropagation();
    
    if (onFriendSelect) {
      onFriendSelect(index, friend);
    }
    
    // Close all dropdowns immediately
    setOpenDropdowns({});
  };

  const renderSquareTable = () => (
    <div className="relative mx-auto" style={{ width: tableSize, height: tableSize }}>
      {/* Table */}
      <div className="absolute inset-8 bg-gradient-to-br from-amber-600 to-amber-800 rounded-lg shadow-lg border-4 border-amber-900">
        {/* Table surface pattern */}
        <div className="absolute inset-2 bg-gradient-to-br from-amber-500 to-amber-700 rounded-md opacity-80"></div>
      </div>
      
      {/* Chairs positioned around table edge */}
      {Array.from({ length: playerCount }).map((_, index) => {
        // Calculate positions based on player count to distribute evenly around square
        let positions = [];
        
        if (playerCount === 2) {
          positions = [
            { top: '-15%', left: '50%', transform: 'translateX(-50%)' }, // Top center
            { bottom: '-15%', left: '50%', transform: 'translateX(-50%)' }, // Bottom center
          ];
        } else if (playerCount === 3) {
          positions = [
            { top: '-15%', left: '50%', transform: 'translateX(-50%)' }, // Top center
            { top: '50%', right: '-15%', transform: 'translateY(-50%)' }, // Right center
            { bottom: '-15%', left: '50%', transform: 'translateX(-50%)' }, // Bottom center
          ];
        } else if (playerCount === 4) {
          positions = [
            { top: '-15%', left: '50%', transform: 'translateX(-50%)' }, // Top center
            { top: '50%', right: '-15%', transform: 'translateY(-50%)' }, // Right center
            { bottom: '-15%', left: '50%', transform: 'translateX(-50%)' }, // Bottom center
            { left: '-15%', top: '50%', transform: 'translateY(-50%)' }, // Left center
          ];
        } else if (playerCount === 5) {
          positions = [
            { top: '-15%', left: '30%', transform: 'translateX(-50%)' }, // Top left
            { top: '-15%', left: '70%', transform: 'translateX(-50%)' }, // Top right
            { top: '50%', right: '-15%', transform: 'translateY(-50%)' }, // Right center
            { bottom: '-15%', left: '50%', transform: 'translateX(-50%)' }, // Bottom center
            { left: '-15%', top: '50%', transform: 'translateY(-50%)' }, // Left center
          ];
        } else if (playerCount === 6) {
          positions = [
            { top: '-15%', left: '30%', transform: 'translateX(-50%)' }, // Top left
            { top: '-15%', left: '70%', transform: 'translateX(-50%)' }, // Top right
            { top: '50%', right: '-15%', transform: 'translateY(-50%)' }, // Right center
            { bottom: '-15%', left: '30%', transform: 'translateX(-50%)' }, // Bottom left
            { bottom: '-15%', left: '70%', transform: 'translateX(-50%)' }, // Bottom right
            { left: '-15%', top: '50%', transform: 'translateY(-50%)' }, // Left center
          ];
        }
        
        return (
          <motion.div
            key={index}
            className="absolute w-20 h-20"
            style={positions[index]}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: index * 0.1 }}
          >
            {/* Chair */}
            <div className="absolute inset-0 bg-gradient-to-br from-brown-600 to-brown-800 rounded-full border-2 border-brown-900 shadow-lg">
              {/* Chair back */}
              <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-4 h-8 bg-gradient-to-br from-brown-700 to-brown-900 rounded-t-full"></div>
            </div>
            
            {/* Person sitting */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div 
                className={`w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full border-2 border-blue-700 shadow-lg flex items-center justify-center transition-all duration-200 relative ${
                  index === 0 ? 'cursor-default' : 
                  !canSelectMore && !selectedFriends[index] ? 'cursor-not-allowed opacity-50' : 
                  'cursor-pointer hover:from-blue-600 hover:to-blue-700'
                }`}
                onClick={() => index !== 0 && canSelectMore && toggleDropdown(index)}
              >
                {index === 0 ? (
                  // First position is always the current user
                  <div className="flex items-center justify-center">
                    <span className="text-lg mr-1">👤</span>
                    <span className="text-white font-bold text-xs">You</span>
                  </div>
                ) : selectedFriends[index] ? (
                  <div className="flex items-center justify-center">
                    <span className="text-lg mr-1">{selectedFriends[index].avatar}</span>
                    <span className="text-white font-bold text-xs">{selectedFriends[index].name}</span>
                  </div>
                ) : (
                  <>
                    {canSelectMore && (
                      <span className="text-yellow-400 text-lg font-bold">+</span>
                    )}
                  </>
                )}
                
                {/* Dropdown */}
                {openDropdowns[index] && (
                  <div 
                    ref={el => dropdownRefs.current[index] = el}
                    className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-50"
                  >
                    <div className="p-2 max-h-48 overflow-y-auto">
                      {friends
                        .filter(friend => !selectedFriends.some(selected => selected && selected.id === friend.id))
                        .map((friend) => (
                                                  <div
                          key={friend.id}
                          className="flex items-center space-x-2 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer transition-colors"
                          onClick={(e) => handleFriendSelect(index, friend, e)}
                        >
                            <span className="text-lg">{friend.avatar}</span>
                            <span className="text-gray-800 dark:text-white text-sm font-medium">{friend.name}</span>
                          </div>
                        ))}
                      {friends.filter(friend => !selectedFriends.some(selected => selected && selected.id === friend.id)).length === 0 && (
                        <div className="p-2 text-gray-500 dark:text-gray-400 text-sm text-center">
                          All friends already selected
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );
      })}
    </div>
  );

  return (
    <div className="relative">
      {renderSquareTable()}
      
      {/* Table center indicator */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-8 h-8 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full border-2 border-yellow-700 shadow-lg flex items-center justify-center">
          <span className="text-yellow-900 font-bold text-xs">Q</span>
        </div>
      </div>
    </div>
  );
};

export default TableLayout; 